import { type DetailedHTMLProps, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { type TextColorType, type TextVariantType, type TextWeightType } from '../types';

type TextHTMLType = DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>;

const variantClasses: Record<TextVariantType, string> = {
  kpi: 'text-2xl leading-8 tracking-[-0.5px]',
  headline: 'text-xl leading-6 tracking-[-0.5px]',
  subhead: 'text-base leading-5 tracking-[-0.03em]',
  label: 'text-base leading-5 tracking-[-0.03em]',
  body1: 'text-base leading-5 tracking-[-0.5px]',
  body2: 'text-sm leading-5 tracking-[-0.5px]',
  footnote: 'text-sm leading-5 tracking-[-0.03em]',
  caption1: 'text-xs leading-4 tracking-[-0.41px]',
  caption2: 'text-[10px] leading-3 tracking-[-0.41px]',
};

const colorClasses: Record<TextColorType, string> = {
  black: 'text-gray-900',
  green: 'text-green-500',
  red: 'text-red-400',
  white: 'text-white',
  grey: 'text-gray-600',
  'light-green': 'text-primary',
  'dark-grey': 'text-gray-900',
  'dark-green': 'text-green-700',
};

const weightClasses: Record<TextWeightType, string> = {
  'extra-light': 'font-extralight',
  light: 'font-light',
  regular: 'font-normal',
  medium: 'font-medium',
  'semi-bold': 'font-semibold',
  bold: 'font-bold',
  'extra-bold': 'font-extrabold',
  'heavy-bold': 'font-black',
};

export type TextProps = Omit<TextHTMLType, 'ref'> & {
  variant: TextVariantType;
  color?: TextColorType;
  weight?: TextWeightType;
  italic?: boolean;
  underline?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  pointer?: boolean;
  value?: ReactNode;
  disabled?: boolean;
};

const getDefaultWeight = (variant: TextVariantType): TextWeightType => {
  switch (variant) {
    case 'subhead':
      return 'bold';
    case 'kpi':
    case 'label':
    case 'footnote':
      return 'semi-bold';
    case 'headline':
    case 'body1':
    case 'body2':
    case 'caption1':
    case 'caption2':
      return 'regular';
  }
};

const Text = ({
  className,
  variant,
  color = 'black',
  weight,
  italic,
  underline,
  uppercase,
  lowercase,
  pointer,
  disabled,
  value,
  children,
  ...rest
}: TextProps) => {
  const resolvedWeight = weight ?? getDefaultWeight(variant);

  return (
    <span
      {...rest}
      className={cn(
        'font-sans',
        variantClasses[variant],
        colorClasses[color],
        weightClasses[resolvedWeight],
        italic && 'italic',
        underline && 'underline',
        uppercase && 'uppercase',
        lowercase && 'lowercase',
        pointer && 'cursor-pointer',
        disabled && 'cursor-not-allowed text-gray-500',
        className,
      )}
    >
      {children ?? value}
    </span>
  );
};

export default Text;
